import { useEffect, useState, useCallback } from "react";
import api from "../services/api";

/**
 * Verifica se o professor tem uma assinatura ativa (necessária pra acessar gerenciamento de alunos).
 * Professor sem assinatura ativa não tem acesso à plataforma (apenas vê o modal pra assinar).
 *
 * hasStudentBillingManagementAccess é mais restrito que hasProfessorBillingAccess:
 * além de ter pagamento válido, o PLANO específico precisa liberar a gestão de
 * pagamentos dos alunos (Plan.unlocks_student_billing — professor pode pagar só
 * o básico, que dá acesso à plataforma mas não à cobrança dos próprios alunos).
 * Espelha app/services/payments/professor_access.py no backend, que é quem
 * de fato gateia os endpoints (este hook só decide o que mostrar na UI).
 *
 * hasActivePlatformSubscription é mais restrito ainda que hasProfessorBillingAccess:
 * só é true quando o professor de fato PAGA a plataforma (is_self_service===true
 * com assinatura/avulso válido) — nunca true no bypass de degustação, mesmo que
 * hasProfessorBillingAccess seja true nesse caso. Usado pra decidir se a aba
 * "Meu pagamento" deve mostrar a assinatura atual (em vez da lista pra assinar).
 *
 * @param {object} me - usuário logado (role, is_self_service)
 * @returns {{ hasProfessorBillingAccess: boolean, hasStudentBillingManagementAccess: boolean, hasActivePlatformSubscription: boolean, loading: boolean, lastSubscription: object|null, lastOneOffAccess: object|null, refetch: function }}
 */
export function useProfessorBillingAccess(me) {
  const isProfessor = me?.role === "professor";

  const [hasProfessorBillingAccess, setHasProfessorBillingAccess] = useState(isProfessor ? false : true);
  const [hasStudentBillingManagementAccess, setHasStudentBillingManagementAccess] = useState(isProfessor ? false : true);
  const [hasActivePlatformSubscription, setHasActivePlatformSubscription] = useState(false);
  const [lastSubscription, setLastSubscription] = useState(null);
  const [lastOneOffAccess, setLastOneOffAccess] = useState(null);
  const [loading, setLoading] = useState(isProfessor);

  const doFetch = useCallback(async () => {
    if (!isProfessor) {
      setHasProfessorBillingAccess(true);
      setHasStudentBillingManagementAccess(true);
      setHasActivePlatformSubscription(false);
      setLastSubscription(null);
      setLastOneOffAccess(null);
      return;
    }

    // Professor de academia: quem paga é a academia (plano por academia, ver
    // app/services/payments/org_billing.py). Só a situação "bloqueada" tira
    // o acesso à gestão; cortesia, em dia e carência liberam. Cobrança dos
    // próprios alunos nunca se aplica a membro de academia.
    if (me?.organization_id && me?.org_role) {
      setHasProfessorBillingAccess(me?.org_billing?.status !== "bloqueada");
      setHasStudentBillingManagementAccess(false);
      setHasActivePlatformSubscription(false);
      setLastSubscription(null);
      setLastOneOffAccess(null);
      setLoading(false);
      return;
    }

    // Bypass: professor em degustação (is_self_service !== true) tem acesso
    // total à plataforma. A gestão de pagamento dos alunos é a exceção — só
    // libera se o admin marcou manualmente student_billing_override=true
    // (degustação parcial: usa a cobrança dos alunos sem pagar a plataforma).
    if (!me?.is_self_service) {
      setHasProfessorBillingAccess(true);
      setHasStudentBillingManagementAccess(!!me?.student_billing_override);
      setHasActivePlatformSubscription(false);
      setLastSubscription(null);
      setLastOneOffAccess(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [subRes, accessRes] = await Promise.all([
        api.get("/payments/subscriptions/me").catch(() => ({ data: null })),
        api.get("/payments/access").catch(() => ({ data: null })),
      ]);

      const subscription = subRes.data;
      const access = accessRes.data;
      const now = new Date();

      // A Asaas marca Subscription.status = "active" já na CRIAÇÃO -
      // antes do Pix do 1º ciclo ser pago (ver professor_access.py::
      // _active_paid_plan, mesma checagem no backend). Sem o check de
      // last_payment, um professor que gera uma assinatura Pix e nunca
      // paga já via "Gerenciar alunos" liberado no front, mesmo o backend
      // rejeitando na hora de usar de verdade. last_payment só vem
      // preenchido quando existe Payment received/confirmed nesse ciclo
      // (ver app/api/payments/subscriptions.py::_last_payment_dict).
      const hasPaidCycle = !!subscription?.last_payment;

      // Ativo OU cancelado mas ainda dentro do período - em ambos os casos,
      // só com ciclo realmente pago.
      const activeSubscription = subscription && subscription.status === "active" && hasPaidCycle;
      const canceledButValidSubscription =
        subscription && subscription.status === "canceled" && hasPaidCycle &&
        subscription.current_period_end && new Date(subscription.current_period_end) > now;

      // Ou tem pagamento avulso válido
      const hasOneOffAccess = access && access.expires_at && new Date(access.expires_at) > now;

      const hasAccess = activeSubscription || canceledButValidSubscription || hasOneOffAccess;

      // unlocks_student_billing ausente/null (assinatura sem plano associado,
      // ou avulso pago antes dessa feature existir) é tratado como liberado —
      // mesmo fallback permissivo do backend, pra não regredir quem já pagava.
      let unlocksStudentBilling = true;
      if (activeSubscription || canceledButValidSubscription) {
        unlocksStudentBilling = subscription?.unlocks_student_billing ?? true;
      } else if (hasOneOffAccess) {
        unlocksStudentBilling = access?.unlocks_student_billing ?? true;
      }

      setHasProfessorBillingAccess(hasAccess);
      setHasStudentBillingManagementAccess(hasAccess && unlocksStudentBilling);
      // Chega aqui só quando is_self_service===true (bypass já retornou acima) —
      // então hasAccess aqui é sempre pagamento real, nunca degustação.
      setHasActivePlatformSubscription(hasAccess);
      setLastSubscription(subscription || null);
      setLastOneOffAccess(hasOneOffAccess ? access : null);
    } catch {
      setHasProfessorBillingAccess(false);
      setHasStudentBillingManagementAccess(false);
      setHasActivePlatformSubscription(false);
      setLastSubscription(null);
      setLastOneOffAccess(null);
    } finally {
      setLoading(false);
    }
  }, [isProfessor, me?.is_self_service, me?.student_billing_override, me?.organization_id, me?.org_role, me?.org_billing?.status]);

  useEffect(() => {
    doFetch();
  }, [isProfessor, me?.id, me?.is_self_service, me?.student_billing_override, me?.org_billing?.status, doFetch]);

  return {
    hasProfessorBillingAccess, hasStudentBillingManagementAccess, hasActivePlatformSubscription,
    loading, lastSubscription, lastOneOffAccess, refetch: doFetch,
  };
}
